import { useContext, useEffect, useRef, useState } from 'react';

import Loader from 'components/loader/Loader';
import VirtualizedEntityList from 'containers/pages/persons/VirtualizedEntityList';
import GroupsBreadCrumbs from 'containers/partials/misc/GroupsBreadCrumbs';
import usePersonStatus from 'hooks/utils/usePersonStatus';
import useFetchAndFilterEntities from 'hooks/queries/useFetchAndFilterEntities';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import { useHistory, useParams } from 'react-router-dom';
import { useContextMenu } from 'contexts/ContextMenuProvider';
import { ButtonExpandable } from 'components/button';
import messages from 'messages';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import Button from 'components/button/Button';
import Icon from 'components/icon/Icon';
import RemoveGroupsButton from 'containers/partials/buttons/RemoveGroupsButton';
import RemoveGroupButton from 'containers/partials/buttons/RemoveGroupButton';
import CopyPersonsModal, {
  PERSON_ACTION
} from 'containers/partials/modals/CopyPersonsModal';
import GroupModal from 'containers/pages/persons/GroupModal';
import Group from 'models/Group';
import Modal from 'components/modal/Modal';
import ImportSportersCard, {
  IMPORT_PERSONS_STATE
} from 'containers/partials/cards/ImportSportersCard';
import { StoreContext } from 'index';
import RemovePersonsFromGroupButton from 'containers/partials/buttons/RemovePersonsFromGroupButton';
import InviteSportersButton from 'containers/partials/buttons/InviteSportersButton';
import {
  createEntityStore,
  EntityStoreContext
} from 'containers/pages/persons/store/EntityStoreContext';
import AddPersonsModal from 'containers/pages/persons/AddPersonsModal';
import { MenuItemsGroup, MenuItemsGroupItem } from 'components/menu';
import GenerateQRCodeModal from 'containers/partials/modals/GenerateQRCodeModal';

const modals = {
  COPY: 'copy',
  MOVE: 'move',
  IMPORT: 'import',
  PERSON: 'person',
  ADD_PERSON: 'add_person',
  GROUP: 'group'
};

const PersonsGroupsTab = ({
  activeId,
  onClick,
  onBreadcrumbClicked,
  onModal = false,
  extraClassNamesForList = {},
  showOnlyGroups = false,
  enableSelectAll = false,
  checkable = true,
  checkableGroups = false,
  showPersonStatus = true,
  extraElement
}) => {
  const {
    authStore: { user }
  } = useContext(StoreContext);
  const intl = useIntl();
  const { push } = useHistory();
  const { entityId, groupId } = useParams();
  const ability = useAbility(AbilityContext);
  const { loading, fetchEntities } = useFetchAndFilterEntities({
    entityId
  });

  const { setMenuItems, addActionButton, clearAllContext } = useContextMenu();

  const group = useEntityContext(s => s.group);
  const groups = useEntityContext(s => s.groups);
  const persons = useEntityContext(s => s.persons);
  const resetFilter = useEntityContext(s => s.resetFilter);
  const clearSelected = useEntityContext(s => s.clearSelected);

  const selectedPersons = useEntityContext(s => s.selectedPersons);
  const selectedGroups = useEntityContext(s => s.selectedGroups);

  const canManageGroups = ability.can('manage', 'Groups');
  const canManagePersons = ability.can('edit', 'Groups');
  const canCreateGroups = ability.can('create', 'Groups');

  const [isRootGroup, setIsRootGroup] = useState(false);
  const [modalSize, setModalSize] = useState(true);

  const [activeModal, setActiveModal] = useState({ type: null, action: null });
  const [showQrModal, setShowQrModal] = useState(false);

  const { personStatusHandler } = usePersonStatus(entityId, groupId);

  const entityModalStore = useRef(createEntityStore()).current;

  useEffect(() => {
    return () => {
      resetFilter();
      clearSelected();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsRootGroup(!groupId || groupId === entityId);
  }, [groupId, group]);

  const AddGroupBtn = ({ isRootGroup }) => (
    <AddGroup
      key={'addGroup'}
      isRootGroup={isRootGroup}
      onClick={() =>
        setActiveModal({
          type: modals.GROUP,
          action: 'add',
          group: group
        })
      }
    />
  );

  const EditGroupBtn = () => (
    <EditGroup
      key={'editGroup'}
      onClick={() =>
        setActiveModal({
          type: modals.GROUP,
          action: 'edit',
          group: group
        })
      }
    />
  );

  const EditSelectedGroupBtn = () => (
    <EditSelectedGroup
      key={'editSelectedGroup'}
      onClick={() =>
        setActiveModal({
          type: modals.GROUP,
          action: 'edit',
          group: selectedGroups[0]
        })
      }
    />
  );

  const ImportPersonsBtn = () => (
    <ImportPersons
      key={'importPersons'}
      onClick={() => setActiveModal({ type: modals.IMPORT })}
      dataQa={'persons-group-context-import'}
    />
  );

  const GenerateQRCodeBtn = () => (
    <Button
      onClick={() => setShowQrModal(true)}
      menu
      disabled={selectedPersons.length === 0}
      dataQa={'generate-qr-code'}
      title={
        selectedPersons.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedPersonsTooltip)
          : null
      }
    >
      <FormattedMessage {...messages.generateQrCode} />
      <Icon id="qr-code" />
    </Button>
  );

  useEffect(() => {
    const contextButtons = [];

    // If we are in a modal, we don't want to apply the context menu items
    if (!onModal) {
      if (canCreateGroups) {
        contextButtons.push(
          <MenuItemsGroup>
            <MenuItemsGroupItem>
              <AddGroupBtn isRootGroup={isRootGroup} />
            </MenuItemsGroupItem>
          </MenuItemsGroup>
        );
      }
      if (!isRootGroup && canManageGroups) {
        contextButtons.push(
          <MenuItemsGroup>
            <MenuItemsGroupItem>
              <EditGroupBtn />
            </MenuItemsGroupItem>
            <MenuItemsGroupItem>
              <RemoveGroup
                key={'removeGroup'}
                entityId={entityId}
                group={group}
              />
            </MenuItemsGroupItem>
          </MenuItemsGroup>
        );
        contextButtons.push(
          <MenuItemsGroup>
            <MenuItemsGroupItem>
              <ImportPersonsBtn />
            </MenuItemsGroupItem>
          </MenuItemsGroup>
        );
      }

      if (canManageGroups) {
        contextButtons.push(
          <MenuItemsGroup>
            <MenuItemsGroupItem>
              <EditSelectedGroupBtn />
            </MenuItemsGroupItem>
            <MenuItemsGroupItem>
              <RemoveSelectedGroups
                key={'removeGroups'}
                entityId={entityId}
                group={group}
              />
            </MenuItemsGroupItem>
          </MenuItemsGroup>
        );
      }

      if (!isRootGroup && canManagePersons) {
        contextButtons.push(
          <MenuItemsGroup>
            <MenuItemsGroupItem>
              <CopyPersons
                key={'copyPersons'}
                onClick={() =>
                  setActiveModal({
                    type: modals.COPY,
                    action: PERSON_ACTION.COPY
                  })
                }
              />
            </MenuItemsGroupItem>
            <MenuItemsGroupItem>
              <MovePersons
                key={'movePersons'}
                onClick={() =>
                  setActiveModal({
                    type: modals.COPY,
                    action: PERSON_ACTION.MOVE
                  })
                }
              />
            </MenuItemsGroupItem>
            <MenuItemsGroupItem>
              <RemovePersonsFromGroup
                key={'removepersons'}
                entityId={entityId}
                group={group}
              />
            </MenuItemsGroupItem>
            <MenuItemsGroupItem>
              <InviteSporters key={'invitePersons'} entityId={entityId} />
            </MenuItemsGroupItem>
          </MenuItemsGroup>
        );

        if (ability.can('manage', 'AutomatedMeasurementProgram')) {
          contextButtons.push(<GenerateQRCodeBtn />);
        }
      }

      setMenuItems(contextButtons);

      if (canCreateGroups && canManagePersons) {
        addActionButton(
          <ButtonExpandable
            tabIndex={0}
            onClick={() => setActiveModal({ type: 'group', action: 'add' })}
            label={intl.formatMessage(messages.addGroupButton, {
              root: isRootGroup
            })}
          />
        );
      }
      if (canManagePersons && !isRootGroup) {
        addActionButton(
          <ButtonExpandable
            tabIndex={0}
            onClick={() => setActiveModal({ type: 'add_person' })}
            label={intl.formatMessage(messages.addPersonsButton)}
          />
        );
      }
    }

    return () => {
      clearAllContext();
    };
  }, [entityId, groupId, group, isRootGroup, selectedGroups, selectedPersons]);

  useEffect(() => {
    resetFilter();
    fetchEntities({ entityId: onModal ? entityId : groupId || entityId });
  }, [entityId, groupId]);

  const onCloseModal = async () => {
    setActiveModal({ type: null, action: null });
  };

  const onDoneModal = async (entityIds, navigate) => {
    setActiveModal({ type: null, action: null });

    if (entityIds && entityIds?.length > 0) {
      for await (const id of entityIds) {
        await fetchEntities({ entityId: id });
      }
    }

    if (navigate) {
      push(navigate);
    }
  };

  const onGroupClickedHandler = async item => {
    await fetchEntities({ entityId: item.id });
  };
  const onBreadcrumbClickedHandler = async id => {
    if (onBreadcrumbClicked) {
      clearSelected();
      onBreadcrumbClicked(id || entityId);
    } else {
      await fetchEntities({ entityId: id });
    }
  };

  const onImportModalStateChange = state => {
    setModalSize(state !== IMPORT_PERSONS_STATE.RESULT);
  };

  if (loading || !group?.id) return <Loader />;

  return (
    <>
      {group.id !== entityId && (
        <GroupsBreadCrumbs
          entityId={entityId}
          groupId={group.id}
          onClick={id => onBreadcrumbClickedHandler(id)}
        />
      )}

      <VirtualizedEntityList
        extraElement={!!extraElement && extraElement}
        id={'persons'}
        entityId={entityId}
        onModal={onModal}
        activeId={activeId}
        items={[...groups, ...persons]}
        enableSelectAll={enableSelectAll}
        checkable={checkable}
        showPersonStatus={showPersonStatus}
        showOnlyGroups={showOnlyGroups}
        checkableGroups={checkableGroups}
        onClick={(item, type) => {
          if (type === 'group') {
            onGroupClickedHandler(item);
          }
          clearSelected();
          onClick && onClick(item, type);
        }}
        onClickPersonStatus={personStatusHandler}
        extraClassNames={extraClassNamesForList}
      />

      {activeModal.type === modals.ADD_PERSON && (
        <EntityStoreContext.Provider value={entityModalStore}>
          <AddPersonsModal
            entityId={entityId}
            group={group}
            onClose={onCloseModal}
            onDone={onDoneModal}
          />
        </EntityStoreContext.Provider>
      )}

      {activeModal.type === modals.GROUP && (
        <GroupModal
          entityId={entityId}
          type={activeModal.action}
          group={
            activeModal.action === 'edit'
              ? activeModal.group
              : new Group({ parentId: groupId })
          }
          parentGroup={group}
          inGroup={group.id !== entityId}
          onClose={onCloseModal}
          onDone={onDoneModal}
        />
      )}

      {activeModal.type === modals.IMPORT && (
        <Modal
          importSporters={!modalSize}
          cardSmall={modalSize}
          autoHeight={modalSize}
          isOpen={true}
          onClose={onCloseModal}
        >
          <ImportSportersCard
            rootEntityId={user.rootEntityId}
            entityId={groupId === 'all' ? entityId : groupId}
            entityName={group.name}
            onClose={reload => onCloseModal(reload)}
            onStateChange={onImportModalStateChange}
          />
        </Modal>
      )}

      {activeModal.type === modals.COPY && (
        <EntityStoreContext.Provider value={entityModalStore}>
          <CopyPersonsModal
            oldEntityId={groupId === 'all' ? entityId : groupId}
            selected={selectedPersons}
            excludedGroups={[groupId]}
            onClose={onCloseModal}
            onDone={onDoneModal}
            action={activeModal.action}
          />
        </EntityStoreContext.Provider>
      )}

      {showQrModal && (
        <GenerateQRCodeModal
          onClose={() => setShowQrModal(false)}
          persons={selectedPersons}
          type="session"
          entityId={entityId}
        />
      )}
    </>
  );
};

export default PersonsGroupsTab;

const EditSelectedGroup = ({ onClick }) => {
  const intl = useIntl();
  const selectedGroups = useEntityContext(s => s.selectedGroups);
  return (
    <Button
      disabled={selectedGroups.length !== 1}
      title={
        selectedGroups.length !== 1
          ? intl.formatMessage(messages.menuItemNoSelectedGroupTooltip)
          : null
      }
      menu
      onClick={onClick}
    >
      <Icon id="edit" />
      <FormattedMessage
        {...messages.menuItemSporterEditSelectedGroup}
        values={{
          count: 1
        }}
      />
    </Button>
  );
};

const RemoveSelectedGroups = ({ entityId }) => {
  const intl = useIntl();
  const selectedGroups = useEntityContext(s => s.selectedGroups);
  return (
    <RemoveGroupsButton
      disabled={selectedGroups.length <= 0}
      title={
        selectedGroups.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedGroupTooltip)
          : null
      }
      menu
      entityId={entityId}
      groups={selectedGroups}
    >
      <FormattedMessage
        {...messages.menuItemSporterRemoveSelectedGroup}
        values={{
          count: selectedGroups.length === 0 ? 1 : selectedGroups.length
        }}
      />
    </RemoveGroupsButton>
  );
};

const AddGroup = ({ onClick, isRootGroup }) => {
  return (
    <Button menu onClick={onClick} dataQa={'add-group'}>
      <Icon id="add-group" />
      <FormattedMessage
        {...messages.addGroupButton}
        values={{
          root: isRootGroup
        }}
      />
    </Button>
  );
};

const EditGroup = ({ onClick }) => {
  return (
    <Button menu onClick={onClick} dataQa={'edit-group'}>
      <Icon id="edit" />
      <FormattedMessage {...messages.menuItemSporterEditGroup} />
    </Button>
  );
};

const RemoveGroup = ({ entityId, group }) => {
  return (
    <RemoveGroupButton
      key={'removeGroup'}
      menu
      entityId={entityId}
      group={group}
      parentId={group.parentId}
    >
      <FormattedMessage {...messages.menuItemSporterRemoveGroup} />
    </RemoveGroupButton>
  );
};

const ImportPersons = ({ onClick, dataQa }) => {
  return (
    <Button key={'importPersons'} menu onClick={onClick} dataQa={dataQa}>
      <FormattedMessage {...messages.buttonSportersImportSporters} />
      <Icon id="add-user" />
    </Button>
  );
};

const CopyPersons = ({ onClick }) => {
  const intl = useIntl();
  const selected = useEntityContext(s => s.selectedPersons);
  return (
    <Button
      disabled={selected.length <= 0}
      title={
        selected.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedPersonsTooltip)
          : null
      }
      menu
      onClick={onClick}
    >
      <FormattedMessage
        {...messages.buttonSportersCopySporters}
        values={{
          count: selected.length === 0 ? 1 : selected.length
        }}
      />
      <Icon id="copy" />
    </Button>
  );
};

const MovePersons = ({ onClick }) => {
  const intl = useIntl();
  const selected = useEntityContext(s => s.selectedPersons);
  return (
    <Button
      disabled={selected.length <= 0}
      title={
        selected.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedPersonsTooltip)
          : null
      }
      menu
      onClick={onClick}
    >
      <FormattedMessage
        {...messages.buttonSportersMoveSporters}
        values={{
          count: selected.length === 0 ? 1 : selected.length
        }}
      />
      <Icon id="move" />
    </Button>
  );
};

const RemovePersonsFromGroup = ({ entityId, group }) => {
  const intl = useIntl();
  const selected = useEntityContext(s => s.selectedPersons);
  const clearSelected = useEntityContext(s => s.clearSelected);

  const onPersonsRemovedFromGroup = async () => {
    clearSelected();
  };

  return (
    <RemovePersonsFromGroupButton
      disabled={selected.length <= 0}
      title={
        selected.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedPersonsTooltip)
          : null
      }
      entityId={group.id}
      rootEntityId={entityId}
      persons={selected}
      menu
      group={group.name}
      onComplete={() => onPersonsRemovedFromGroup(group.id)}
    >
      <FormattedMessage
        {...messages.menuItemSporterArchiveSporters}
        values={{
          count: selected.length === 0 ? 1 : selected.length
        }}
      />
    </RemovePersonsFromGroupButton>
  );
};

const InviteSporters = ({ entityId }) => {
  const intl = useIntl();
  const selected = useEntityContext(s => s.selectedPersons);
  return (
    <InviteSportersButton
      disabled={selected.length <= 0}
      title={
        selected.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedPersonsTooltip)
          : null
      }
      menu
      entityId={entityId}
      persons={selected}
    >
      <FormattedMessage
        {...messages.buttonSportersInvite}
        values={{
          count: selected.length === 0 ? 1 : selected.length
        }}
      />
    </InviteSportersButton>
  );
};
